// @ts-nocheck

import React, { useRef, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Target } from '../../client';
import MatchCardAnimationContainer from '../../modules/animations/containers/MatchCardAnimationContainer';
import { progressivelyOpenLineUpView } from '../../modules/line-up/actions/lineUp';
import LineUpOpenViewButton from '../../modules/line-up/components/LineUpOpenViewButton/LineUpOpenViewButton';
import { SportsWithLineUpNewTag } from '../../modules/line-up/constants/runtime';
import theme from '../../styled-components/dark';
import { type SportId } from '../../types/sports';
import BetGroupFactoryContainer from '../BetGroupFactory/BetGroupFactoryContainer';
import Link from '../Link';
import CardHeaderContainer from '../MatchCardHeader/MatchCardHeaderContainer';
import MatchCardMoreBetsButton from '../MatchCardMoreBetsButton';
import MatchFullTitle from '../MatchFullTitle';

const StyledLink = styled(Link)`
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  font-size: ${Target.__MOBILE__ ? 1 : 1.1}em;
  position: relative;
`;

const Wrapper = styled.div`
  margin: ${Target.__MOBILE__ ? '0px 5px' : '0'};
  padding: ${Target.__MOBILE__ ? '0 5px 10px 5px' : '10px'};
  transform: translate3d(0, 0, 0);
`;

const WrapTitle = styled.div`
  position: relative;
  margin: 0;
`;

const StyledMatchFullTitle = styled(MatchFullTitle)`
  user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

const StyledCardHeaderContainer = styled(CardHeaderContainer)`
  font-size: 0.8em;
  z-index: 10;
  position: relative;
`;

const InnerWrap = styled.div`
  padding: 0 0 0.3em 0;
  margin: 0 0;
  border-radius: 0 0 ${theme.Card.borderRadius} ${theme.Card.borderRadius};
  background: ${theme.MatchCard.backgroundColor};
`;

const AbsoluteRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.div`
  width: 100%;
  padding-top: 33.33%;
  position: relative;
  pointer-events: none;
  z-index: 9;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  pointer-events: none;
`;

const StyledBetGroupFactoryContainer = styled(BetGroupFactoryContainer)`
  .bet-group-template {
    padding-bottom: 0;
  }

  .bet-group-line {
    margin: 0 0.6em;
  }
  .expand-button {
    margin: 0.6em 0.6em 0;
  }
`;

type MatchCardProps = {
  className?: string;
  startDate: number;
  isFavorite: boolean;
  showFavorite: boolean;
  sportId: SportId;
  image: string | null;
  moreBets: number;
  tvChannel: string | null;
  flagId: string;
  betsData: Array<{ betId: number; isMultichoice: boolean }>;
  matchId: number;
  competitor1Name: string;
  competitor2Name: string;
  competitor1NbRedCards?: number;
  competitor2NbRedCards?: number;
  score1: number | null;
  score2: number | null;
  flag1: string | null;
  flag2: string | null;
  subTitle: string | null;
  isLive?: boolean | null;
  periodName?: string;
  to: string;
  psoScore: Array<string> | null;
  areMatchLineUpsAvailable?: boolean;
  expandId: string;
};

const MatchCard = ({
  className,
  moreBets = 0,
  flagId,
  matchId,
  betsData,
  startDate,
  tvChannel,
  image,
  competitor1Name,
  competitor2Name,
  competitor1NbRedCards = 0,
  competitor2NbRedCards = 0,
  score1,
  score2,
  flag1,
  flag2,
  subTitle,
  sportId,
  isLive = false,
  periodName,
  to,
  psoScore,
  areMatchLineUpsAvailable,
  expandId,
}: MatchCardProps) => {
  const wrapperRef = useRef();
  const dispatch = useDispatch();

  const onLineUpRedirection = useCallback(
    (e) => {
      e?.preventDefault();
      e?.stopPropagation();
      dispatch(progressivelyOpenLineUpView(matchId));
    },
    [dispatch, matchId],
  );

  const hasLineUpNewTag = useMemo(() => SportsWithLineUpNewTag.indexOf(Number(sportId)) !== -1, [sportId]);

  return (
    <Wrapper className={className} ref={wrapperRef} data-testid={`match-card-${matchId}`}>
      <StyledCardHeaderContainer
        matchId={matchId}
        isLive={isLive}
        sportId={sportId}
        startDate={startDate}
        tvChannel={tvChannel}
        periodName={periodName}
        cardBadgeHasAttach
      />
      <InnerWrap>
        <StyledLink to={to}>
          {image ? (
            <ImageContainer>
              <Image src={image} />
            </ImageContainer>
          ) : null}
          <WrapTitle>
            <StyledMatchFullTitle
              matchId={matchId}
              sportId={sportId}
              isLive={isLive}
              competitor1Name={competitor1Name}
              competitor2Name={competitor2Name}
              competitor1NbRedCards={competitor1NbRedCards}
              competitor2NbRedCards={competitor2NbRedCards}
              score1={isLive ? score1 : null}
              score2={isLive ? score2 : null}
              flag1={flag1}
              flag2={flag2}
              padScoreboard={!image}
              subTitle={subTitle}
              flagId={flagId}
              isMatchCard
              psoScore={psoScore}
            />
            <AbsoluteRight>
              {areMatchLineUpsAvailable ? (
                <LineUpOpenViewButton onClick={onLineUpRedirection} isNew={hasLineUpNewTag} sportId={sportId} />
              ) : (
                <MatchCardMoreBetsButton moreBets={moreBets} />
              )}
            </AbsoluteRight>
          </WrapTitle>
        </StyledLink>
        <StyledBetGroupFactoryContainer
          expandId={expandId}
          betsData={betsData}
          matchId={matchId}
          showAlternativeTitle
        />
        <MatchCardAnimationContainer matchId={matchId} />
      </InnerWrap>
    </Wrapper>
  );
};

export default React.memo<MatchCardProps>(MatchCard);
